import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Lightbulb, Wand2, Building2, Ruler } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import ProHeader from "@/components/pro/ProHeader";
import ProHero from "@/components/pro/ProHero";
import PartidasTable from "@/components/pro/PartidasTable";
import ResultsPanel from "@/components/pro/ResultsPanel";
import SavedBudgets from "@/components/pro/SavedBudgets";
import { Toaster } from "@/components/ui/sonner";

import { PARTIDAS_DEFAULT, GLOBAL_OPT_PARTIDAS, TIPOS_OBRA } from "@/data/pro-partidas";
import { listBudgets, saveBudget, deleteBudget, getBudget, newId } from "@/lib/pro-storage";
import { exportBudgetPDF } from "@/lib/pro-pdf";
import { COMPANY } from "@/data/services";

function eur(n) { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0)); }

function buildPartidas() {
  return PARTIDAS_DEFAULT.map(p => ({
    ...p,
    incluir: true,
    modo: 'estandar',
    precioFinal: p.estandar,
  }));
}

export default function ProBudget() {
  const [id, setId] = useState(newId());
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [workType, setWorkType] = useState(TIPOS_OBRA[0].label);
  const [surface, setSurface] = useState(80);
  const [notes, setNotes] = useState("");
  const [partidas, setPartidas] = useState(buildPartidas());
  const [savedList, setSavedList] = useState([]);
  const baselineRef = useRef({});

  // Snapshot baseline standard prices on mount/reset
  useEffect(() => {
    baselineRef.current = Object.fromEntries(PARTIDAS_DEFAULT.map(p => [p.id, p.estandar]));
  }, []);

  useEffect(() => {
    setSavedList(listBudgets());
  }, []);

  const totals = useMemo(() => {
    const incluidas = partidas.filter(p => p.incluir);
    const subtotal = incluidas.reduce((s, p) => s + p.precioFinal * surface, 0);
    const iva = subtotal * 0.10;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  }, [partidas, surface]);

  const insights = useMemo(() => {
    const out = [];
    const incluidas = partidas.filter(p => p.incluir);
    if (!incluidas.length) {
      out.push({ tone: 'warning', text: 'No hay partidas activas. Activa al menos una para empezar.' });
      return out;
    }
    const totalEstandar = incluidas.reduce((s, p) => s + p.estandar * surface, 0);
    const subtotal = totals.subtotal;
    if (totalEstandar > 0 && subtotal / totalEstandar < 0.85) {
      out.push({ tone: 'info', text: 'Presupuesto muy ajustado (más de 15% bajo media). Asegúrate de cubrir costes y márgenes reales.' });
    }
    const masCara = [...incluidas].sort((a, b) => (b.precioFinal * surface) - (a.precioFinal * surface))[0];
    if (masCara) {
      const importe = masCara.precioFinal * surface;
      if (masCara.modo === 'estandar' && masCara.ajustable) {
        const ahorro = (masCara.estandar - masCara.optimizado) * surface;
        out.push({ tone: 'opportunity', text: `${masCara.name} representa la partida más cara (${eur(importe)}). Pasarla a Optimizado ahorra ${eur(ahorro)}.` });
      }
    }
    if (surface < 40) {
      out.push({ tone: 'info', text: 'Superficie pequeña: considera aplicar un mínimo de intervención o un recargo por baja medición.' });
    } else if (surface > 150) {
      out.push({ tone: 'opportunity', text: 'Superficie grande: tienes margen para negociar materiales y aplicar precios optimizados.' });
    }
    return out;
  }, [partidas, surface, totals.subtotal]);

  const handleApplyOptimized = () => {
    setPartidas(prev => prev.map(p => GLOBAL_OPT_PARTIDAS.includes(p.id) ? { ...p, modo: 'optimizado', precioFinal: p.optimizado } : p));
    toast.success("Versión optimizada aplicada a las partidas ajustables");
  };

  const handleResetAll = () => {
    setPartidas(buildPartidas());
    toast.message("Partidas restauradas a precio estándar");
  };

  const handleSave = () => {
    const payload = {
      id,
      clientName, clientPhone, clientAddress, workType,
      surface: Number(surface) || 0,
      notes,
      partidas,
      totalConIva: totals.total,
      updatedAt: Date.now(),
    };
    saveBudget(payload);
    setSavedList(listBudgets());
    toast.success(`Presupuesto guardado: ${clientName || 'Sin nombre'}`);
  };

  const handleLoad = (bid) => {
    const b = getBudget(bid);
    if (!b) return;
    setId(b.id);
    setClientName(b.clientName || "");
    setClientPhone(b.clientPhone || "");
    setClientAddress(b.clientAddress || "");
    setWorkType(b.workType || TIPOS_OBRA[0].label);
    setSurface(b.surface || 80);
    setNotes(b.notes || "");
    setPartidas(b.partidas || buildPartidas());
    toast.success(`Cargado: ${b.clientName || 'Sin nombre'}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (bid) => {
    deleteBudget(bid);
    setSavedList(listBudgets());
    toast.message("Presupuesto eliminado");
  };

  const handleNew = () => {
    setId(newId());
    setClientName("");
    setClientPhone("");
    setClientAddress("");
    setWorkType(TIPOS_OBRA[0].label);
    setSurface(80);
    setNotes("");
    setPartidas(buildPartidas());
    toast.message("Nuevo presupuesto");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExport = async () => {
    if (!surface || surface <= 0) {
      toast.error("Indica una superficie válida en m²");
      return;
    }
    toast.message("Generando PDF…");
    await exportBudgetPDF({
      id, clientName, clientPhone, clientAddress, workType,
      surface: Number(surface), notes, partidas,
      updatedAt: Date.now(),
    });
    toast.success("PDF exportado");
  };

  return (
    <div className="min-h-screen bg-slate-50 dark-bg-page text-slate-900 dark-text-strong">
      <ProHeader />
      <ProHero />

      <main className="container mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Cabecera de proyecto */}
        <div className="rounded-2xl border border-slate-200 dark-border bg-white dark-bg-card p-5 md:p-7 mb-8" data-testid="pro-project-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">Datos del proyecto</div>
              <div className="font-display font-semibold text-slate-900 dark-text-strong text-xl">Información del cliente y obra</div>
            </div>
            <div className="text-[11px] text-slate-400 dark-text-mid">ID: {id}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 mt-5">
            <Field className="md:col-span-3" id="cn" label="Cliente">
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Ej: Familia García" data-testid="pro-client-name" className="dark-bg-section dark-text-strong dark-border focus-visible:ring-emerald-500 focus-visible:ring-offset-0" />
            </Field>
            <Field className="md:col-span-2" id="cp" label="Teléfono">
              <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="612 345 678" type="tel" data-testid="pro-client-phone" className="dark-bg-section dark-text-strong dark-border focus-visible:ring-emerald-500 focus-visible:ring-offset-0" />
            </Field>
            <Field className="md:col-span-3" id="ad" label="Dirección de la obra">
              <Input value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} placeholder="Calle, número, Valencia" data-testid="pro-client-address" className="dark-bg-section dark-text-strong dark-border focus-visible:ring-emerald-500 focus-visible:ring-offset-0" />
            </Field>
            <Field className="md:col-span-2" id="wt" label="Tipo de obra">
              <select value={workType} onChange={(e) => setWorkType(e.target.value)} data-testid="pro-work-type" className="w-full h-10 px-3 rounded-md border border-slate-200 dark-border bg-white dark-bg-section text-sm text-slate-900 dark-text-strong focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 focus:outline-none">
                {TIPOS_OBRA.map(t => <option key={t.id} value={t.label}>{t.label}</option>)}
              </select>
            </Field>
            <Field className="md:col-span-2" id="sf" label="Superficie (m²)" icon={Ruler}>
              <Input type="number" min="1" step="1" value={surface} onChange={(e) => setSurface(Number(e.target.value))} data-testid="pro-surface" className="font-display font-semibold text-lg h-10 dark-bg-section dark-text-strong dark-border focus-visible:ring-emerald-500 focus-visible:ring-offset-0" />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Columna principal */}
          <div className="xl:col-span-8 space-y-6">
            <PartidasTable
              partidas={partidas}
              surface={Number(surface) || 0}
              onChange={setPartidas}
              onApplyOptimized={handleApplyOptimized}
              onResetAll={handleResetAll}
            />

            {/* Insights */}
            <div className="rounded-2xl border border-slate-200 dark-border bg-white dark-bg-card p-5 md:p-6" data-testid="pro-insights">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-emerald-600" />
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark-text-mid font-semibold">Insights y recomendaciones</div>
              </div>
              {insights.length === 0 ? (
                <div className="text-sm text-slate-500 dark-text-mid">Todo en orden. Tu presupuesto se ajusta a la media de mercado.</div>
              ) : (
                <ul className="space-y-2">
                  {insights.map((it, i) => (
                    <li key={i} data-testid={`insight-${i}`} className={[
                      "text-sm leading-relaxed p-3 rounded-lg border flex items-start gap-2",
                      it.tone === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                      it.tone === 'opportunity' ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark-bg-section dark-border dark-text-accent' :
                      'bg-slate-50 dark-bg-section border-slate-200 dark-border text-slate-700 dark-text-mid',
                    ].join(" ")}>
                      <Wand2 className="w-4 h-4 shrink-0 mt-0.5 opacity-70" />
                      <span>{it.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Notas */}
            <div className="rounded-2xl border border-slate-200 dark-border bg-white dark-bg-card p-5 md:p-6">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark-text-mid font-semibold">Notas privadas / observaciones</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Detalles internos: condiciones, materiales acordados, plazos…"
                data-testid="pro-notes"
                className="mt-2 w-full rounded-lg border border-slate-200 dark-border bg-white dark-bg-section text-slate-900 dark-text-strong p-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 focus:outline-none"
              />
            </div>

            {/* Export CTA */}
            <button
              type="button"
              onClick={handleExport}
              data-testid="pro-export-pdf-btn"
              className="w-full inline-flex items-center justify-center gap-3 h-14 px-6 rounded-xl bg-slate-900 dark-bg-card-strong text-white font-display font-semibold text-base hover:bg-slate-800 transition-colors shadow-lg"
            >
              <FileDown className="w-5 h-5" />
              Exportar presupuesto a PDF profesional
            </button>
          </div>

          {/* Columna lateral */}
          <aside className="xl:col-span-4 space-y-6">
            <div className="xl:sticky xl:top-20 space-y-6">
              <ResultsPanel partidas={partidas} surface={Number(surface) || 0} baselineStandard={baselineRef.current} />
              <SavedBudgets
                list={savedList}
                current={id}
                onLoad={handleLoad}
                onDelete={handleDelete}
                onNew={handleNew}
                onSave={handleSave}
              />
            </div>
          </aside>
        </div>

        <p className="text-xs text-slate-400 dark-text-mid text-center mt-12 max-w-3xl mx-auto leading-relaxed">
          <Building2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Budget Calc Pro · Herramienta interna para profesionales · Los precios son orientativos y deben validarse con visita técnica. Los datos se guardan únicamente en este navegador (localStorage).
        </p>
      </main>

      {/* Pie de página con datos de empresa */}
      <footer className="mt-10 bg-slate-900 dark-bg-card-strong text-white">
        <div className="container mx-auto px-4 md:px-8 py-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5 flex items-start gap-4">
            <img src={COMPANY.logo} alt={COMPANY.name} className="w-14 h-14 rounded-lg object-contain bg-white p-1" />
            <div>
              <div className="font-display font-bold text-lg">{COMPANY.name}</div>
              <div className="text-emerald-300 text-sm italic mt-0.5">{COMPANY.slogan}</div>
              <div className="text-slate-300 text-sm mt-2">{COMPANY.tagline}</div>
              <div className="text-slate-400 text-xs mt-1">{COMPANY.area}</div>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-semibold mb-3">Contacto</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>WhatsApp / Tel: <span className="text-white font-medium">{COMPANY.whatsappFormatted}</span></li>
              <li>Email: <span className="text-white font-medium">{COMPANY.email}</span></li>
              <li>Web: <span className="text-white font-medium">{COMPANY.website}</span></li>
              <li>Instagram: <span className="text-white font-medium">@{COMPANY.instagram}</span></li>
            </ul>
          </div>
          <div className="md:col-span-3 text-xs text-slate-400 leading-relaxed">
            Empresa S.L.U. legalmente registrada · Equipo propio, personal asegurado · Garantía 2 años sobre mano de obra.
            <p className="mt-3 text-slate-500">Atendemos por WhatsApp, teléfono y visitas presenciales en obra. No disponemos de oficina física abierta al público.</p>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4 text-[11px] text-slate-400">
            <div>© {new Date().getFullYear()} {COMPANY.name}. Todos los derechos reservados.</div>
            <div>Budget Calc Pro · Generador interno de presupuestos · Datos en localStorage</div>
          </div>
        </div>
      </footer>

      <Toaster position="top-right" richColors />
    </div>
  );
}

function Field({ id, label, children, className, icon: Icon }) {
  return (
    <div className={className}>
      <Label htmlFor={id} className="text-[10px] uppercase tracking-wider text-slate-500 dark-text-mid font-semibold flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
